import { IconCloudUpload, IconPencil, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Entry, Transcript } from '@/types/api';
import { type Clause, transcriptSegments } from '@/utils/transcriptSegments';
import { formatDate, formatDateTime } from './format';

type Props = {
  transcripts: Transcript[];
  /** Entries of the same visit, to mark what was taken from where. */
  entries: Entry[];
  /** What the microphone hears right now (not final yet). */
  interim?: string;
  /** An earlier visit: no editing, no assigning. */
  readOnly?: boolean;
  onTooth: (tooth: number) => void;
  onAssign: (transcript: Transcript, text: string) => void;
  onEdit: (transcript: Transcript, text: string) => Promise<boolean>;
};

/** Short language label for a BCP 47 tag: sr-RS → SR. */
const langLabel = (lang: string) => lang.split('-')[0]?.toUpperCase() ?? '';

/** The clauses of entries that came from this dictation (or were assigned from it). */
function clausesOf(transcript: Transcript, entries: Entry[]): Clause[] {
  return entries
    .filter(
      (e) =>
        e.status !== 'REJECTED' &&
        e.transcript &&
        (e.transcriptId ? e.transcriptId === transcript.id : transcript.local)
    )
    .map((e) => ({ text: e.transcript ?? '', tooth: e.tooth }));
}

function Line({
  line,
  clauses,
  readOnly,
  onTooth,
  onAssign,
}: {
  line: string;
  clauses: Clause[];
  readOnly?: boolean;
  onTooth: (tooth: number) => void;
  onAssign: () => void;
}) {
  const { t } = useTranslation();
  const segments = transcriptSegments(line, clauses);
  return (
    <p className="dictation-text__line">
      {segments.map((s, i) =>
        s.teeth.length ? (
          <button
            // biome-ignore lint/suspicious/noArrayIndexKey: segments of a fixed line
            key={i}
            type="button"
            className="dictation-text__mark"
            onClick={() => onTooth(s.teeth[0])}
            title={t('chart.transcript.showTooth')}
          >
            {s.text}
            <span className="dictation-text__teeth">{s.teeth.join(', ')}</span>
          </button>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: segments of a fixed line
          <span key={i}>{s.text}</span>
        )
      )}
      {!readOnly && (
        <button
          type="button"
          className="icon-btn icon-btn--sm dictation-text__assign"
          onClick={onAssign}
          aria-label={t('chart.transcript.assign')}
          title={t('chart.transcript.assign')}
        >
          <IconPlus size={16} />
        </button>
      )}
    </p>
  );
}

function Editor({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (text: string) => Promise<boolean>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(initial);
  const [busy, setBusy] = useState(false);
  return (
    <div className="stack">
      <textarea
        className="input"
        rows={Math.min(12, Math.max(3, text.split('\n').length + 1))}
        value={text}
        onChange={(e) => setText(e.target.value)}
        aria-label={t('chart.transcript.editLabel')}
      />
      <div className="row">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={busy || text === initial}
          onClick={async () => {
            setBusy(true);
            if (await onSave(text)) onCancel();
            setBusy(false);
          }}
        >
          {t('common.save')}
        </button>
        <button type="button" className="btn btn--sm" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

function TranscriptBlock({
  transcript,
  entries,
  readOnly,
  onTooth,
  onAssign,
  onEdit,
}: Omit<Props, 'transcripts' | 'interim'> & { transcript: Transcript }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const clauses = clausesOf(transcript, entries);
  const lines = transcript.text.split('\n').filter((l) => l.trim());
  // Edits need the server's copy (and its id).
  const canEdit = !(readOnly || transcript.local);

  return (
    <div className="dictation-text">
      <div className="dictation-text__meta muted small">
        <span className="badge" title={transcript.lang}>
          {langLabel(transcript.lang)}
        </span>
        {transcript.local && (
          <span title={t('chart.transcript.unsent')}>
            <IconCloudUpload size={14} aria-label={t('chart.transcript.unsent')} />
          </span>
        )}
        {transcript.editedBy && (
          <span>
            {t('chart.transcript.edited', {
              name: transcript.editedBy.name,
              at: formatDateTime(transcript.editedAt),
            })}
          </span>
        )}
        <span className="grow" />
        {canEdit && !editing && (
          <button
            type="button"
            className="icon-btn icon-btn--sm"
            onClick={() => setEditing(true)}
            aria-label={t('chart.transcript.edit')}
            title={t('chart.transcript.edit')}
          >
            <IconPencil size={16} />
          </button>
        )}
      </div>
      {editing ? (
        <Editor
          initial={transcript.text}
          onSave={(text) => onEdit(transcript, text)}
          onCancel={() => setEditing(false)}
        />
      ) : (
        lines.map((line, i) => (
          <Line
            // biome-ignore lint/suspicious/noArrayIndexKey: lines of one text
            key={i}
            line={line}
            clauses={clauses}
            readOnly={readOnly}
            onTooth={onTooth}
            onAssign={() => onAssign(transcript, line)}
          />
        ))
      )}
    </div>
  );
}

/**
 * What was dictated during one visit, in the language it was said in. The
 * author and time are shown once; the parts that became entries are marked
 * with their teeth. A line can be assigned to a tooth by hand.
 */
export function TranscriptCard({ transcripts, interim, ...rest }: Props) {
  const { t } = useTranslation();
  const first = transcripts[0];
  const authors = [...new Set(transcripts.map((x) => x.createdBy.name))].join(', ');
  return (
    <section className="card">
      <div className="card__head">
        <h2>{t('chart.transcript.title')}</h2>
        {first && (
          <span className="muted small">
            {authors} · {formatDate(first.createdAt)}
          </span>
        )}
      </div>
      {transcripts.length === 0 && !interim && (
        <p className="muted small">{t('chart.transcript.empty')}</p>
      )}
      {transcripts.map((x) => (
        <TranscriptBlock key={x.clientId} transcript={x} {...rest} />
      ))}
      {interim && <p className="dictation-text__line transcript__interim">{interim}</p>}
    </section>
  );
}
